import React, { useState } from 'react';

interface StatAbbreviation {
  abbreviation: string;
  fullName: string;
  definition: string;
}

interface VideoChapter {
  timestamp: string;
  topic: string;
  focus: string;
}

const PRO_TIP_VIDEO_ID = '4RS9iYwW-FU';

const proTipVideoChapters: VideoChapter[] = [
  { timestamp: '0:15', topic: 'Teams Report', focus: 'High-level tactical overview' },
  { timestamp: '2:10', topic: 'Players Report', focus: 'Individual performance deep-dives' },
  { timestamp: '3:01', topic: 'Dynamic Leaderboard', focus: 'Real-time ranking and motivation' },
  { timestamp: '3:25', topic: 'Roster Adjustments', focus: 'Managing your lineup on the fly' },
  { timestamp: '3:49', topic: 'Stat Corrections', focus: 'Ensuring 100% data integrity' },
  { timestamp: '4:40', topic: 'Data Sharing', focus: 'The bridge to college recruiting' },
  { timestamp: '5:30', topic: 'In-App Guidance', focus: 'Navigating your tools with ease' },
];

const CoachCorner: React.FC = () => {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const statAbbreviations: StatAbbreviation[] = [
    {
      abbreviation: 'G',
      fullName: 'Goals',
      definition: 'A shot that completely crosses the goal line.',
    },
    {
      abbreviation: 'GPG',
      fullName: 'Goals Per Game',
      definition: 'Average goals scored per game.',
    },
    {
      abbreviation: 'A',
      fullName: 'Assists',
      definition: 'A pass to a teammate that leads directly to a goal without the scorer needing to dodge an opponent.',
    },
    {
      abbreviation: 'P',
      fullName: 'Points',
      definition: "The sum of a player's Goals and Assists.",
    },
    {
      abbreviation: 'PPG',
      fullName: 'Points Per Game',
      definition: 'Average points scored per game.',
    },
    {
      abbreviation: 'S',
      fullName: 'Shots',
      definition: 'Any ball propelled toward the goal with the intent of scoring.',
    },
    {
      abbreviation: 'SS',
      fullName: 'Shots Saved',
      definition: 'Shots taken and saved by the goalie.',
    },
    {
      abbreviation: 'S%',
      fullName: 'Shot Percentage',
      definition: 'The percentage of shots that result in goals.',
    },
    {
      abbreviation: 'SOG%',
      fullName: 'SOG Percentage',
      definition: 'The percentage of shots that are on target.',
    },
    {
      abbreviation: 'TO',
      fullName: 'Turnover',
      definition: 'When a player or team loses possession of the ball to the opponent.',
    },
    {
      abbreviation: 'TO/PG',
      fullName: 'Turnovers Per Game',
      definition: 'Average Turnovers per game.',
    },
    {
      abbreviation: 'EMOS',
      fullName: 'Extra Man Offense Success',
      definition: "Goals scored while the team has a numerical advantage due to an opponent's penalty.",
    },
    {
      abbreviation: 'EMOF',
      fullName: 'Extra Man Offense Failure',
      definition: 'Failure to score a goal during EMO opportunity.',
    },
    {
      abbreviation: 'EMOS%',
      fullName: 'Extra Man Offense Success Percentage',
      definition: 'The ratio of EMO goals scored to EMO opportunities.',
    },
    {
      abbreviation: 'MDDF',
      fullName: 'Man-Down Defense Failure',
      definition: 'Goals scored while the team is playing with fewer players due to a penalty.',
    },
    {
      abbreviation: 'MDDS',
      fullName: 'Man Down Defense Success',
      definition: 'No goals allowed during man down situations.',
    },
    {
      abbreviation: 'MDDS%',
      fullName: 'Man Down Defense Success Percentage',
      definition: 'The ratio of MDDS to Man Down situations.',
    },
    {
      abbreviation: 'GB',
      fullName: 'Ground Ball',
      definition: 'Gaining possession of a loose ball during live play.',
    },
    {
      abbreviation: 'CTO',
      fullName: 'Caused Turnovers',
      definition: "When a defensive player's positive action (check, interception, etc.) forces a turnover.",
    },
    {
      abbreviation: 'CTO/PG',
      fullName: 'Caused Turnovers Per Game',
      definition: 'Average Caused Turnovers per game.',
    },
    {
      abbreviation: 'SV',
      fullName: 'Saves',
      definition: 'When a goalie stops a shot that would have otherwise entered the goal.',
    },
    {
      abbreviation: 'SV%',
      fullName: 'Save Percentage',
      definition: 'The ratio of saves to total shots on goal faced.',
    },
    {
      abbreviation: 'SPG',
      fullName: 'Saves Per Game',
      definition: 'Average number of saves a goalie makes per game.',
    },
    {
      abbreviation: 'GA',
      fullName: 'Goals Allowed',
      definition: 'The number of goals scored against a goalie while he is in the net.',
    },
    {
      abbreviation: 'GAPG',
      fullName: 'Goals Allowed Per Game',
      definition: 'Average number of goals a goalie allows per game.',
    },
    {
      abbreviation: 'FO',
      fullName: 'Faceoffs',
      definition: 'Total Faceoffs',
    },
    {
      abbreviation: 'W%',
      fullName: 'Faceoff Win Pct',
      definition: 'Percentage of faceoffs won.',
    },
    {
      abbreviation: 'GP',
      fullName: 'Games Played',
      definition: 'Total number of games in which each player participated.',
    },
  ];

  const toggleSection = (sectionId: string) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-green-50 via-white to-emerald-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-700 text-white">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
              </div>
            </div>
            <div>
              <h1 className="text-xl font-bold">Coach's Corner</h1>
              <p className="text-green-100 text-base mt-1">Advanced metrics, KPIs, and stat references for coaches</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* NPC Section */}
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
          <button
            onClick={() => toggleSection('npc')}
            className="w-full p-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">What is NPC?</h2>
                <p className="text-sm text-gray-500 mt-1">Net Possession Contribution - STATDOG's unique metric</p>
              </div>
            </div>
            <svg
              className={`w-6 h-6 text-gray-400 transition-transform ${expandedSection === 'npc' ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {expandedSection === 'npc' && (
            <div className="px-6 pb-6 border-t border-gray-200">
              <div className="pt-6 space-y-4">
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                  <p className="text-lg font-semibold text-blue-900 mb-2">NPC = Net Possession Contribution</p>
                  <p className="text-gray-700">
                    NPC is the unique metric in STATDOG that measures a player's complete positive and negative impact
                    on team possession.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <p className="text-sm font-medium text-gray-500 mb-2">The Calculation:</p>
                  <p className="text-xl font-mono font-bold text-gray-900">
                    NPC = Ground Balls − Net Turnovers (TOs-CTOs) − Shots Saved
                  </p>
                </div>

                <div className="space-y-3 text-gray-700 leading-relaxed">
                  <p>
                    This metric moves beyond simple scoring, to quantify the value of gaining possessions, caused
                    turnovers, and minimizing saved shots (which often function as turnovers).
                  </p>

                  <p>
                    <strong>NPC reveals the hidden value</strong> of your non-scoring contributors, allowing coaches to
                    identify the high-impact players that traditional statistics often miss. Conversely, NPC reveals the
                    degree to which your guys with scoring stats might be bleeding the ball to your opponents via TOs
                    and shots saved.
                  </p>

                  <div className="bg-green-50 rounded-xl p-4 border border-green-200 mt-4">
                    <p className="font-semibold text-green-800 mb-2">💡 Key Insight</p>
                    <p className="text-green-700">
                      Statistically speaking, every possession equates to <strong>0.25 of a goal</strong>... So, guys
                      with high NPC stats are "scoring" for you without even taking a shot... guys with negative NPC
                      stats are putting points on the board for the other team!
                    </p>
                  </div>

                  <p className="italic text-gray-600 mt-4">
                    Showing this "report card" to young men is impactful and I have seen doing so change habits and
                    improve decision making!
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Pro Tip Video — Unlocking the Full Power of STATDOG */}
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
          <button
            onClick={() => toggleSection('protip')}
            className="w-full p-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Unlocking the Full Power of STATDOG</h2>
                <p className="text-sm text-gray-500 mt-1">Pro Tip Video</p>
              </div>
            </div>
            <svg
              className={`w-6 h-6 text-gray-400 transition-transform ${
                expandedSection === 'protip' ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {expandedSection === 'protip' && (
            <div className="px-6 pb-6 border-t border-gray-200">
              <div className="pt-6 space-y-6">
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    You put in the work on the field—now make sure that effort translates into a measurable advantage.
                    This short walkthrough is designed to help you master the STATDOG ecosystem, moving beyond basic
                    tracking into high-level strategy.
                  </p>
                  <p>
                    We&apos;ll show you how to leverage real-time KPIs to pivot mid-game, analyze season-long trends to
                    find your team&apos;s ceiling, and use our data-sharing tools to put your players directly on the
                    radar of college recruiters.
                  </p>
                </div>

                <div className="relative w-full overflow-hidden rounded-xl border border-gray-200 bg-black aspect-video">
                  <iframe
                    className="absolute inset-0 h-full w-full"
                    src={`https://www.youtube.com/embed/${PRO_TIP_VIDEO_ID}`}
                    title="Unlocking the Full Power of STATDOG"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>

                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Video Chapters</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 w-28">
                            Timestamp
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 w-48">
                            Topic
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200">
                            Focus
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {proTipVideoChapters.map((chapter, index) => (
                          <tr key={chapter.timestamp} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-4 py-3 text-sm font-bold text-gray-900">{chapter.timestamp}</td>
                            <td className="px-4 py-3 text-sm font-semibold text-gray-900">{chapter.topic}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{chapter.focus}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="border-l-4 border-amber-400 bg-amber-50 rounded-r-xl p-4">
                  <p className="text-gray-800">
                    <strong>Pro Tip:</strong> Pay close attention to the <strong>Data Sharing</strong> section at 4:40.
                    This is the primary feature used to export verified metrics directly to college coaching staffs.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* KPIs Section */}
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
          <button
            onClick={() => toggleSection('kpi')}
            className="w-full p-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  How does STATDOG use Key Performance Indicators (KPIs)?
                </h2>
                <p className="text-sm text-gray-500 mt-1">Understanding metrics that drive success</p>
              </div>
            </div>
            <svg
              className={`w-6 h-6 text-gray-400 transition-transform ${expandedSection === 'kpi' ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {expandedSection === 'kpi' && (
            <div className="px-6 pb-6 border-t border-gray-200">
              <div className="pt-6 space-y-4">
                <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                  <p className="text-gray-700">
                    A <strong>Key Performance Indicator (KPI)</strong> is a quantifiable measure used to track progress
                    towards specific goals, helping teams understand their performance and make data-driven decisions to
                    achieve strategic objectives.
                  </p>
                </div>

                <p className="text-gray-700">
                  KPIs focus on the most critical metrics that drive success, differentiating them from general metrics
                  by aligning directly with strategic outcomes and guiding focused action.
                </p>

                <p className="text-gray-700">
                  STATDOG translates complex game elements into measurable, actionable KPIs. Beyond goals, we emphasize
                  metrics like:
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {[
                    'Time of Possession',
                    'Turnovers',
                    'Shots Taken',
                    'Shots Allowed',
                    'EMO Success',
                    'Clearing Percentage',
                    'Goalie Save %',
                    'Ground Balls',
                  ].map((metric) => (
                    <div
                      key={metric}
                      className="bg-gray-50 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 border border-gray-200 text-center"
                    >
                      {metric}
                    </div>
                  ))}
                </div>

                <p className="text-gray-700">
                  These KPIs break the game into manageable areas of focus so coaches and players can act on them.
                </p>

                <div className="mt-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">How KPIs Work & Why They Matter</h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                        <h4 className="font-semibold text-gray-900">Track Progress</h4>
                      </div>
                      <p className="text-sm text-gray-600">
                        They provide checkpoints to see if you're on the right path to a goal.
                      </p>
                    </div>

                    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                        </div>
                        <h4 className="font-semibold text-gray-900">Data-Driven Decisions</h4>
                      </div>
                      <p className="text-sm text-gray-600">
                        They replace gut feelings with evidence, leading to smarter choices.
                      </p>
                    </div>

                    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                            />
                          </svg>
                        </div>
                        <h4 className="font-semibold text-gray-900">Priorities</h4>
                      </div>
                      <p className="text-sm text-gray-600">
                        They ensure daily tasks connect to broader team objectives.
                      </p>
                    </div>

                    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </svg>
                        </div>
                        <h4 className="font-semibold text-gray-900">Focus</h4>
                      </div>
                      <p className="text-sm text-gray-600">
                        They highlight areas needing attention, helping focus efforts.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Stat Abbreviations Section */}
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
          <button
            onClick={() => toggleSection('abbreviations')}
            className="w-full p-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Stat Abbreviations in STATDOG</h2>
                <p className="text-sm text-gray-500 mt-1">Quick reference for all statistical abbreviations</p>
              </div>
            </div>
            <svg
              className={`w-6 h-6 text-gray-400 transition-transform ${
                expandedSection === 'abbreviations' ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {expandedSection === 'abbreviations' && (
            <div className="px-6 pb-6 border-t border-gray-200">
              <div className="pt-6">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 w-24">
                          Abbrev.
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 w-48">
                          Full Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200">
                          Definition
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {statAbbreviations.map((stat, index) => (
                        <tr key={stat.abbreviation} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-3 text-sm font-bold text-green-600">{stat.abbreviation}</td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{stat.fullName}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{stat.definition}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoachCorner;
